// Global chat. Rendered in the hub's right panel and in the social
// drawer's Tavern tab; both read the same store, so the two mounts never
// disagree. Rows go through the shared CommentRow so chat, deck notes and
// replay threads stay one surface. Mentions are server-resolved: the
// body is rewritten to `[#name](player:id)` links before render so the
// inline markdown pass turns them into profile pills.
import { Component, createRef } from 'preact';
import { CommentRow, CommentComposer, ComposerBar, ReplyReference, continuesPrevious } from './comments/CommentThread';
import * as chat from '../utils/globalChatStore';
import { resolveAvatarUrl } from '../utils/arena/avatarUtils';
import AvatarImage from './ui/AvatarImage';
import { isPingSeen, markPingSeen } from '../utils/arena/tavernSeen';
import CardLinkPreview, { cardLinkAnchorRect } from './CardLinkPreview';
import ReplayShareCard, { replayMetaFromHistory } from './ReplayShareCard';
import { PopoverPanel, PopoverMenuItem, PopoverMenuHeader } from './ui/popoverMenu';
import { replayableMatches } from '../utils/arena/matchHistory';
import { fetchReplaySummary } from '../utils/game/replay/replayApi';
import { sendChatMessage } from '../utils/arena/chatApi';
import { innerRadius } from '../lib/medievalTheme';
import { getCanvasScale } from '../utils/uiScale';
import { playUI, UI } from '../utils/arena/uiSounds';
import { GOLD, ACCENT_GOLD, TEXT_MUTED, TEXT_BODY, MENU_BG } from '../lib/medievalTheme';
import MarkdownEditor from './MarkdownEditor';
import { toast } from 'sonner';
import { t } from '../localization/t';

// Gap between the panel wall and the composer. The hub panel's radius is
// --radius-xl (0.75cqw), so the gutter has to stay under that or the
// concentric inner radius collapses to 0. 0.375cqw leaves a 0.375cqw corner.
const COMPOSER_GAP = '0.375cqw';
const HIGHLIGHT_MS = 2500;
// Pages of older history fetched looking for a deep-linked message that
// has left the buffer, before giving up.
const JUMP_PAGES_MAX = 4;

// Thread open/close: the view slides in over this long, from one canvas
// twelfth aside. Opacity only under reduced motion.
const VIEW_SWAP_MS = 200;
const VIEW_SWAP_SHIFT_PX = 12;
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';
// A shared replay's card, under the message body. Capped so the hub's wide
// panel does not blow a 16:9 tile up to the full column.
const REPLAY_CARD_MAX_WIDTH = '18cqw';

function ReplayIcon() {
  return (
    <svg style={{ width: '0.9375cqw', height: '0.9375cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M10 8.5 15.5 12 10 15.5z" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function bodyWithMentionLinks(body, mentions) {
  let out = body || '';
  for (const m of mentions || []) {
    if (!m?.name || !m?.id) continue;
    const re = new RegExp(`(^|\\s)#${m.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\w.-])`, 'gi');
    out = out.replace(re, `$1[#${m.name}](player:${m.id})`);
  }
  return out;
}

// Threads are reply chains: a thread's root is the topmost loaded ancestor
// of any message in it, and it holds every message whose own chain reaches
// that root. A parent that has paged out of the buffer simply makes the
// oldest loaded reply the root.
function rootIn(byId, m) {
  const seen = new Set();
  while (m.replyTo?.id && byId.has(m.replyTo.id) && !seen.has(m.id)) { seen.add(m.id); m = byId.get(m.replyTo.id); }
  return m.id;
}

export function rootOf(messages, id) {
  const byId = new Map(messages.map((m) => [m.id, m]));
  return byId.has(id) ? rootIn(byId, byId.get(id)) : null;
}

// Search matches the body or the author, case-insensitively.
export function matchesQuery(m, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (m.body || '').toLowerCase().includes(q) || (m.playerName || '').toLowerCase().includes(q);
}

// Every message in `id`'s thread, in feed order (root first when loaded).
export function threadOf(messages, id) {
  const byId = new Map(messages.map((m) => [m.id, m]));
  const start = byId.get(id);
  if (!start) return [];
  const root = rootIn(byId, start);
  return messages.filter((m) => rootIn(byId, m) === root);
}

// rootId -> { count, repliers } for every loaded thread with replies, so a
// root can wear its "N replies" pill. Repliers are unique, first three.
export function threadStats(messages) {
  const byId = new Map(messages.map((m) => [m.id, m]));
  const out = new Map();
  for (const m of messages) {
    if (!m.replyTo) continue;
    const root = rootIn(byId, m);
    if (root === m.id) continue;
    const stat = out.get(root) || { count: 0, repliers: [] };
    stat.count += 1;
    if (stat.repliers.length < 3 && !stat.repliers.some((r) => r.playerId === m.playerId)) {
      stat.repliers.push({ playerId: m.playerId, playerName: m.playerName, playerAvatar: m.playerAvatar });
    }
    out.set(root, stat);
  }
  return out;
}

function ThreadPill({ stat, sorceryCards, onClick }) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-2 cursor-pointer transition-colors"
      onClick={onClick}
      style={{ marginTop: '0.35cqw', padding: '0.15cqw 0.45cqw 0.15cqw 0.2cqw', borderRadius: 'var(--radius-sm)', background: 'transparent', border: '1px solid transparent' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; e.currentTarget.style.borderColor = `${GOLD} 0.25)`; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}
    >
      <span className="flex items-center">
        {stat.repliers.map((r, i) => (
          <span
            key={r.playerId || i}
            className="overflow-hidden flex items-center justify-center"
            style={{ width: '0.95cqw', height: '0.95cqw', borderRadius: '50%', marginLeft: i ? '-0.3cqw' : 0, border: '1px solid #0e0a06', background: 'rgba(180, 140, 60, 0.14)', color: ACCENT_GOLD, fontSize: '0.45cqw', fontWeight: 700 }}
          >
            {r.playerAvatar ? <AvatarImage src={resolveAvatarUrl({ profileAvatar: r.playerAvatar }, sorceryCards)} className="w-full h-full" /> : (r.playerName || '?').slice(0, 1).toUpperCase()}
          </span>
        ))}
      </span>
      <span className="font-semibold text-[0.58cqw]" style={{ color: ACCENT_GOLD }}>{stat.count === 1 ? t('1 reply') : t('{n} replies', { n: stat.count })}</span>
    </button>
  );
}

// In the hub header the well keeps to one width; the drawer's slim top
// row gives it the whole line.
// A click on the message itself: not on one of its controls or links,
// not inside its editor, and not the mouse-up of a text selection.
function isPlainRowClick(e) {
  if (e.target.closest('button, a, input, textarea, [contenteditable], [role="button"], .chat-tools')) return false;
  return !(window.getSelection?.()?.toString());
}

function SearchField({ value, placeholder, onChange, wide = false }) {
  return (
    <div className={`tavern-search relative flex items-center ${wide ? 'flex-1' : 'shrink-0'}`} style={{ height: '1.6cqw', width: wide ? undefined : '11cqw', borderRadius: 'var(--radius-md)', background: 'rgba(0, 0, 0, 0.3)', border: `1px solid ${GOLD} 0.2)`, boxShadow: 'inset 0 1px 4px rgba(0, 0, 0, 0.45)' }}>
      <svg className="absolute pointer-events-none" style={{ left: '0.45cqw', width: '0.75cqw', height: '0.75cqw', color: `${GOLD} 0.5)` }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        className="min-w-0 flex-1 h-full bg-transparent border-none outline-none text-[0.6cqw]"
        style={{ color: TEXT_BODY, caretColor: ACCENT_GOLD, padding: '0 1.4cqw 0 1.45cqw' }}
        onInput={(e) => onChange(e.target.value)}
        // Escape empties the box first; a second one falls through to the
        // window handler and leaves the thread.
        onKeyDown={(e) => { if (e.key === 'Escape' && value) { e.stopPropagation(); onChange(''); } }}
      />
      {value ? (
        <button
          type="button"
          aria-label={t('Clear search')}
          className="absolute flex items-center justify-center cursor-pointer"
          style={{ right: '0.3cqw', width: '1cqw', height: '1cqw', borderRadius: 'var(--radius-sm)', background: 'transparent', border: 'none', color: TEXT_MUTED, padding: 0 }}
          onClick={() => onChange('')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" style={{ width: '0.6cqw', height: '0.6cqw' }}>
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      ) : null}
    </div>
  );
}

export default class GlobalChatView extends Component {
  state = { chat: chat.getState(), draft: '', replyTo: null, hoveredId: null, pinnedToBottom: true, pendingBelow: 0, posting: false, cardHover: null, highlightedId: null, replayPicker: false, replayPicking: false, replayDraft: null, threadId: null, pickerId: null, freshPings: new Set(), editingId: null, editDraft: '', query: '', viewingOlder: false, forwardId: null, forwardQuery: '', composerFocus: 0 };
  _pingEls = new Map();
  _list = createRef();
  _view = createRef();

  componentDidMount() {
    this._unsub = chat.subscribe(this.handleStore);
    chat.setSelf(this.props.profile?.id);
    chat.setVisible(true);
    if (!this.state.chat.hydrated) chat.hydrate();
    // A deep link owns the first scroll position; the usual pin to the
    // bottom would cancel its scrollIntoView.
    if (this.props.highlightId) this.jumpTo(this.props.highlightId);
    else this.scrollToBottom();
    window.addEventListener('keydown', this.handleKeyDown);
    // A ping's tint clears once the row has actually been on screen.
    this._io = typeof IntersectionObserver === 'function'
      ? new IntersectionObserver(this.handlePingsVisible, { root: this._list.current, threshold: 0.6 })
      : null;
  }

  componentDidUpdate() {
    // Re-render once a second while a server timeout is counting down,
    // then drop the error on its own once it lifts.
    const { waitUntil } = this.state.chat;
    if (waitUntil && waitUntil > Date.now() && !this._waitTimer) {
      this._waitTimer = setInterval(() => {
        if (this.state.chat.waitUntil <= Date.now()) chat.clearError();
        else this.forceUpdate();
      }, 1000);
    } else if ((!waitUntil || waitUntil <= Date.now()) && this._waitTimer) {
      clearInterval(this._waitTimer);
      this._waitTimer = null;
    }
  }

  componentWillUnmount() {
    this._unmounted = true;
    this._unsub?.();
    clearInterval(this._waitTimer);
    clearTimeout(this._postTimer);
    clearTimeout(this._highlightTimer);
    this._viewAnim?.cancel();
    chat.setVisible(false);
    window.removeEventListener('keydown', this.handleKeyDown);
    this._io?.disconnect();
  }

  handleKeyDown = (e) => {
    if (e.key !== 'Escape') return;
    if (this.state.editingId) { this.cancelEdit(); return; }
    if (this.state.pickerId) { this.setState({ pickerId: null }); return; }
    if (this.state.forwardId) { this.setState({ forwardId: null }); return; }
    if (this.state.threadId && !this.state.replayPicker) this.closeThread();
  };

  // Ref callback on a fresh ping's row: watch it until it has been seen.
  observePing = (el, id) => {
    if (!el || this._pingEls.get(id) === el) return;
    this._pingEls.set(id, el);
    el.dataset.pingId = id;
    if (this._io) this._io.observe(el); else this.settlePings([id]);
  };

  handlePingsVisible = (entries) => {
    const ids = entries.filter((en) => en.isIntersecting).map((en) => en.target.dataset.pingId);
    for (const en of entries) if (en.isIntersecting) this._io.unobserve(en.target);
    if (ids.length) this.settlePings(ids);
  };

  // Remember the pings as seen, but keep them tinted for the rest of this
  // visit: the tint goes on the next open, not while you are reading.
  settlePings = (ids) => {
    for (const id of ids) markPingSeen(id);
    this.setState((st) => { const next = new Set(st.freshPings); for (const id of ids) next.add(id); return { freshPings: next }; });
  };

  pingsMe = (m) => {
    const myId = this.props.profile?.id;
    return !!myId && m.playerId !== myId && (m.replyTo?.playerId === myId || !!m.mentions?.some((x) => x.id === myId));
  };

  // Inline edit of one of your own messages, Discord-style: the body turns
  // into an editor in place; Enter saves, Escape cancels, an unchanged or
  // emptied draft just cancels.
  startEdit = (m) => this.setState({ editingId: m.id, editDraft: m.body || '', pickerId: null });

  cancelEdit = () => this.setState({ editingId: null, editDraft: '' });

  saveEdit = () => {
    const { editingId, editDraft, chat: s } = this.state;
    const m = s.messages.find((x) => x.id === editingId);
    const body = editDraft.trim();
    if (m && body && body !== (m.body || '')) chat.edit(m.id, body);
    this.cancelEdit();
  };

  threadRootMessage = () => {
    const { threadId, chat: s } = this.state;
    if (!threadId) return null;
    const root = rootOf(s.messages, threadId);
    return s.messages.find((m) => m.id === root) || null;
  };

  // Thread mode: the root pins at the top, the list shows its replies, and
  // the composer answers the root unless a specific reply is picked.
  // A Tavern search does not follow you in: the thread shows whole. It is
  // still there on the way back.
  openThread = (id) => {
    const root = rootOf(this.state.chat.messages, id) || id;
    const rootMsg = this.state.chat.messages.find((m) => m.id === root) || null;
    if (rootMsg && this.pingsMe(rootMsg)) this.settlePings([root]);
    this.setState({ threadId: root, replyTo: rootMsg, pickerId: null, pinnedToBottom: true, query: '' }, this.scrollToBottom);
  };

  // The thread sits one level below the Tavern, so it arrives from the
  // right and the Tavern comes back from the left. The composer stays
  // put; only the view above it moves.
  animateViewSwap = (deeper) => {
    const el = this._view.current;
    if (!el || typeof el.animate !== 'function') return;
    this._viewAnim?.cancel();
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const from = reduce ? 0 : (deeper ? 1 : -1) * VIEW_SWAP_SHIFT_PX * getCanvasScale();
    this._viewAnim = el.animate(
      [{ opacity: 0, transform: `translateX(${from}px)` }, { opacity: 1, transform: 'translateX(0)' }],
      { duration: VIEW_SWAP_MS, easing: EASE_OUT },
    );
  };

  closeThread = () => {
    this.setState({ threadId: null, replyTo: null, pickerId: null, pinnedToBottom: true }, this.scrollToBottom);
  };

  // Deep link from a notification: scroll the message into view and hold
  // a gold wash on it for a beat. Retries once the buffer has hydrated,
  // then pages older history in case the message has scrolled out of it.
  jumpTo = (id) => {
    if (!id) return;
    if (id !== this._jumpId) { this._jumpId = id; this._jumpPages = 0; }
    const el = this._list.current?.querySelector?.(`[data-comment-id="${id}"]`);
    if (!el) {
      const s = this.state.chat;
      this._pendingJump = id;
      if (!s.hydrated || s.loadingOlder) return;
      if (s.hasMore && this._jumpPages < JUMP_PAGES_MAX) {
        this._jumpPages += 1;
        // A page that adds rows re-enters through componentDidUpdate; an
        // empty one never changes state, so it has to give up here.
        chat.loadOlder().then((added) => { if (!added && this._pendingJump === id) this.giveUpJump(); });
        return;
      }
      this.giveUpJump();
      return;
    }
    this._pendingJump = null;
    el.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    clearTimeout(this._highlightTimer);
    this.setState({ highlightedId: id, pinnedToBottom: false, viewingOlder: true });
    this._highlightTimer = setTimeout(() => this.setState({ highlightedId: null }), HIGHLIGHT_MS);
  };

  // Forward: the message goes to a friend as a `tavern` direct message.
  // The server snapshots the author and body from the id; the body sent
  // here is the fallback a server that predates the type stores as text.
  openForward = (m) => this.setState({ forwardId: m.id, forwardQuery: '', pickerId: null });

  forwardTo = async (friend) => {
    const m = this.state.chat.messages.find((x) => x.id === this.state.forwardId);
    this.setState({ forwardId: null });
    if (!m) return;
    try {
      await sendChatMessage(friend.id, {
        type: 'tavern',
        body: `Forwarded from ${m.playerName || 'the Tavern'} in the Tavern: ${m.body}`,
        metadata: { messageId: m.id },
      });
      toast.success(t('Forwarded to {name}', { name: friend.name }));
    } catch (err) {
      toast.error(err?.message || t('Could not forward that message.'));
    }
  };

  // A search hit leads to the message in its place: the filter drops and
  // the list scrolls to the row, held in gold for a beat.
  openResult = (id) => this.setState({ query: '' }, () => this.jumpTo(id));

  jumpToPresent = () => {
    this.setState({ pinnedToBottom: true, viewingOlder: false });
    this.scrollToBottom();
  };

  giveUpJump = () => {
    this._pendingJump = null;
    if (!this._unmounted) toast.error(t('That message is no longer in the Tavern.'));
  };

  findCard = (cardId) => (this.props.sorceryCards || []).find((c) => c.unique_id === cardId) || null;

  handleCardLinkClick = (cardId) => {
    const card = this.findCard(cardId);
    if (card) this.props.onOpenCard?.(card);
  };

  handleCardLinkHover = (cardId, element) => {
    const card = this.findCard(cardId);
    const rect = cardLinkAnchorRect(element);
    if (card && rect) this.setState({ cardHover: { card, rect } });
  };

  handleCardLinkHoverEnd = () => {
    if (this.state.cardHover) this.setState({ cardHover: null });
  };

  // Everyone who has spoken recently plus the viewer's friends. Enough
  // for the # autocomplete without a directory endpoint.
  mentionPlayers() {
    const { sorceryCards, friends = [] } = this.props;
    const out = new Map();
    for (const m of this.state.chat.messages) {
      if (m.playerId && !out.has(m.playerId)) {
        out.set(m.playerId, { id: m.playerId, name: m.playerName, avatarUrl: resolveAvatarUrl({ profileAvatar: m.playerAvatar }, sorceryCards), meta: t('in the tavern') });
      }
    }
    for (const f of friends) {
      if (f?.id && f?.name && !out.has(f.id)) {
        out.set(f.id, { id: f.id, name: f.name, avatarUrl: resolveAvatarUrl(f, sorceryCards), meta: f.online ? t('friend, online') : t('friend') });
      }
    }
    return [...out.values()];
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.highlightId && this.props.highlightId !== prevProps.highlightId) this.jumpTo(this.props.highlightId);
    if (prevState.threadId !== this.state.threadId) this.animateViewSwap(!!this.state.threadId);
    if (prevState.chat.messages === this.state.chat.messages) return;
    // A pending jump owns this pass: pinnedToBottom has not flipped yet,
    // so the bottom pin below would cancel its scrollIntoView.
    if (this._pendingJump) { this.jumpTo(this._pendingJump); return; }
    if (this.state.pinnedToBottom) {
      this.scrollToBottom();
    } else if (this.state.chat.messages.length > prevState.chat.messages.length) {
      this.setState((s) => ({ pendingBelow: s.pendingBelow + 1 }));
    }
  }

  handleStore = (next) => {
    // The send button unlocks on the server echo (own message landed)
    // or on a rejection.
    this.setState((s) => ({ chat: next, posting: next.error ? false : s.posting }));
  };

  scrollToBottom = () => {
    const el = this._list.current;
    if (el) el.scrollTop = el.scrollHeight;
    if (this.state.pendingBelow) this.setState({ pendingBelow: 0 });
  };

  handleScroll = () => {
    const el = this._list.current;
    if (!el) return;
    // More than a screen above the bottom counts as reading the past.
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const viewingOlder = distance > el.clientHeight;
    if (viewingOlder !== this.state.viewingOlder) this.setState({ viewingOlder });
    if (el.scrollTop < 40 && this.state.chat.hasMore && !this.state.chat.loadingOlder && this.state.chat.hydrated) {
      // Keep the viewport anchored on the same message while older rows
      // land above it.
      const prevHeight = el.scrollHeight;
      chat.loadOlder().then((added) => {
        if (added && this._list.current) this._list.current.scrollTop += this._list.current.scrollHeight - prevHeight;
      });
    }
    const atBottom = distance < 24;
    if (atBottom === this.state.pinnedToBottom) return;
    // Scrolling back down clears the "n new messages" jump button;
    // scrolling away leaves whatever has piled up alone.
    this.setState(atBottom ? { pinnedToBottom: true, pendingBelow: 0 } : { pinnedToBottom: false });
  };

  // The same snapshot the DM replay picker sends (see ChatView
  // .handleSendReplay): the row plus the archive's Avatar matchup, which is
  // the only way a solo match shows two decks instead of one name twice.
  pickReplay = async (m) => {
    this.setState({ replayPicking: true });
    const summary = await fetchReplaySummary(m.roomCode).catch(() => null);
    if (this._unmounted) return;
    this.setState({
      replayPicking: false,
      replayPicker: false,
      replayDraft: { ...replayMetaFromHistory(m, this.props.profile), matchAvatars: summary?.avatars || null, solo: !!summary?.solo },
    });
  };

  handleSubmit = () => {
    const { draft, replyTo, replayDraft } = this.state;
    if (!draft.trim() && !replayDraft) return;
    if (chat.post(draft, replyTo?.id || null, replayDraft)) {
      playUI(UI.CLICK);
      this.setState({ draft: '', replyTo: this.threadRootMessage(), replayDraft: null, posting: true, pinnedToBottom: true });
      // Rate-limit errors come back as chat:global:error; a normal post
      // echoes as chat:global:message. Either way the composer unlocks.
      clearTimeout(this._postTimer);
      this._postTimer = setTimeout(() => this.setState({ posting: false }), 1500);
    }
  };

  render() {
    const { profile, sorceryCards, onViewProfile, onWatchReplay, canModerate = false, compact = false } = this.props;
    const { chat: s, draft, replyTo, hoveredId, pendingBelow, posting, cardHover, highlightedId, replayPicker, replayPicking, replayDraft, threadId, pickerId, forwardId, forwardQuery } = this.state;
    const forwardNeedle = forwardQuery.trim().toLowerCase();
    const forwardFriends = (this.props.friends || []).filter((f) => f?.id && f?.name && (!forwardNeedle || f.name.toLowerCase().includes(forwardNeedle)));
    const myId = profile?.id;
    const byId = new Map(s.messages.map((m) => [m.id, m]));
    const root = threadId ? rootOf(s.messages, threadId) : null;
    const rootMsg = root ? byId.get(root) : null;
    const messages = threadId ? threadOf(s.messages, threadId).filter((m) => m.id !== root) : s.messages;
    const stats = threadId ? null : threadStats(s.messages);
    const replyingToRoot = !!threadId && !!replyTo && replyTo.id === root;
    const { freshPings, editingId, editDraft, query, viewingOlder, pinnedToBottom } = this.state;
    const showJump = !pinnedToBottom && (viewingOlder || pendingBelow > 0);
    const searching = !!query.trim();
    const shown = searching ? messages.filter((m) => matchesQuery(m, query)) : messages;
    const search = (
      <SearchField
        wide={compact}
        value={query}
        placeholder={threadId ? t('Search this thread…') : t('Search the Tavern…')}
        onChange={(v) => this.setState({ query: v })}
      />
    );

    // A search hit leads to the message; otherwise a click on the message
    // itself opens its thread. Inside a thread the rows are the thread.
    const rowClick = searching ? this.openResult : threadId ? null : this.openThread;
    const renderRow = (m, i, list, pinned = false) => {
      const row = { ...m, body: bodyWithMentionLinks(m.body, m.mentions) };
      const continued = !pinned && continuesPrevious(list[i - 1], m);
      const fresh = this.pingsMe(m) && (freshPings.has(m.id) || !isPingSeen(m.id));
      const stat = stats?.get(m.id);
      return (
        <div
          key={m.id}
          ref={fresh ? (el) => this.observePing(el, m.id) : undefined}
          className={rowClick ? 'cursor-pointer' : undefined}
          onClick={rowClick ? (e) => { if (isPlainRowClick(e)) rowClick(m.id); } : undefined}
        >
          <CommentRow
            comment={row}
            continued={continued}
            avatarUrl={resolveAvatarUrl({ profileAvatar: m.playerAvatar }, sorceryCards)}
            sorceryCards={sorceryCards}
            canDelete={canModerate || m.playerId === myId}
            isHovered={hoveredId === m.id}
            onHoverChange={(id) => this.setState({ hoveredId: id })}
            onOpenProfile={onViewProfile}
            onPlayerClick={onViewProfile}
            onDelete={(id) => chat.remove(id)}
            deletePrompt={t('delete this message?')}
            onReply={(c) => this.setState((st) => ({ replyTo: c, composerFocus: st.composerFocus + 1 }))}
            onOpenThread={threadId ? undefined : () => this.openThread(m.id)}
            onForward={m.body ? this.openForward : undefined}
            onEdit={m.playerId === myId && editingId !== m.id ? () => this.startEdit(m) : undefined}
            editor={editingId === m.id ? (
              <div style={{ marginTop: '0.15cqw' }}>
                <div style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${GOLD} 0.35)`, borderRadius: 'var(--radius-md)', padding: '0.4cqw 0.55cqw' }}>
                  <MarkdownEditor
                    value={editDraft}
                    onChange={(v) => this.setState({ editDraft: v })}
                    onSubmit={this.saveEdit}
                    autoFocus
                    sorceryCards={sorceryCards}
                    mentionPlayers={this.mentionPlayers()}
                    onOpenCard={this.handleCardLinkClick}
                    onCardLinkHover={this.handleCardLinkHover}
                    onCardLinkHoverEnd={this.handleCardLinkHoverEnd}
                    style={{ color: TEXT_BODY, lineHeight: 1.4, maxHeight: '8cqw' }}
                  />
                </div>
                <div className="text-[0.55cqw]" style={{ color: TEXT_MUTED, marginTop: '0.2cqw' }}>
                  {t('escape to')} <button type="button" className="cursor-pointer" style={{ color: ACCENT_GOLD, background: 'transparent', border: 'none', padding: 0 }} onClick={this.cancelEdit}>{t('cancel')}</button>
                  {' \u00b7 '}{t('enter to')} <button type="button" className="cursor-pointer" style={{ color: ACCENT_GOLD, background: 'transparent', border: 'none', padding: 0 }} onClick={this.saveEdit}>{t('save')}</button>
                </div>
              </div>
            ) : null}
            showLike={false}
            reactions={m.reactions || []}
            onReact={(emoji) => chat.react(m.id, emoji)}
            pickerOpen={pickerId === m.id}
            onPickerToggle={(open) => this.setState({ pickerId: open ? m.id : null })}
            onCardClick={this.handleCardLinkClick}
            onCardHover={this.handleCardLinkHover}
            onCardHoverEnd={this.handleCardLinkHoverEnd}
            highlighted={highlightedId === m.id || fresh}
            attachment={m.replay ? (
              <div style={{ maxWidth: REPLAY_CARD_MAX_WIDTH, marginTop: '0.4cqw' }}>
                <ReplayShareCard meta={m.replay} sorceryCards={sorceryCards} canWatch={!!onWatchReplay} onWatch={onWatchReplay} />
              </div>
            ) : null}
            footer={stat ? <ThreadPill stat={stat} sorceryCards={sorceryCards} onClick={() => this.openThread(m.id)} /> : null}
            reply={m.replyTo && !(threadId && m.replyTo.id === root) ? (
              <ReplyReference
                avatarUrl={resolveAvatarUrl({ profileAvatar: byId.get(m.replyTo.id)?.playerAvatar }, sorceryCards)}
                name={m.replyTo.playerName}
                preview={m.replyTo.bodyPreview}
                onClick={() => this.openThread(m.replyTo.id)}
              />
            ) : null}
          />
        </div>
      );
    };
    const replays = replayableMatches(profile?.matchHistory);
    const waitLeft = s.waitUntil ? Math.max(0, Math.ceil((s.waitUntil - Date.now()) / 1000)) : 0;
    const waitLabel = waitLeft > 120
      ? t('until {time}', { time: new Date(s.waitUntil).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })
      : waitLeft > 0 ? t('{n}s', { n: waitLeft }) : '';
    return (
      <div className="flex-1 min-h-0 flex flex-col">
        {compact ? (
          <div className="shrink-0 flex" style={{ padding: `0.5cqw ${COMPOSER_GAP} 0.25cqw` }}>{search}</div>
        ) : (
          <>
            <div className="px-5 pt-5 pb-3 shrink-0 flex items-center gap-2">
              <span className="arena-heading text-sm font-semibold" style={{ color: `${GOLD} 0.55)`, textShadow: '0 0 12px rgba(180,140,60,0.15)' }}>
                {t('Tavern')}
              </span>
              <span className="ml-auto flex">{search}</span>
            </div>
            <div className="mx-5 h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD} 0.2), transparent)` }} />
          </>
        )}

        <div ref={this._view} className="flex-1 min-h-0 flex flex-col">
        {threadId ? (
          <>
            <div className="shrink-0 flex items-center gap-2" style={{ padding: '0.4cqw 1.25cqw', borderBottom: `1px solid ${GOLD} 0.15)` }}>
              {/* The chevron and the word are one control: the whole
                  title is the way back, not just the small arrow. */}
              <button
                type="button"
                className="flex items-center gap-1 cursor-pointer transition-colors"
                title={t('Back to the Tavern (Esc)')}
                aria-label={t('Back to the Tavern')}
                onClick={this.closeThread}
                style={{ height: '1.4cqw', padding: '0 0.45cqw 0 0.25cqw', marginLeft: '-0.25cqw', borderRadius: 'var(--radius-md)', background: 'transparent', border: 'none', color: TEXT_MUTED }}
                onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.1)`; e.currentTarget.style.color = ACCENT_GOLD; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = TEXT_MUTED; }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.8cqw', height: '0.8cqw' }}>
                  <path d="M15 5l-7 7 7 7" />
                </svg>
                <span className="arena-heading font-semibold text-[0.75cqw]" style={{ color: ACCENT_GOLD }}>{t('Thread')}</span>
              </button>
              <span className="text-[0.6cqw]" style={{ color: TEXT_MUTED }}>{messages.length === 1 ? t('1 reply') : t('{n} replies', { n: messages.length })}</span>
            </div>
          </>
        ) : null}

        <div className="relative flex-1 min-h-0">
          <div ref={this._list} className="absolute inset-0 overflow-y-auto pt-4" onScroll={this.handleScroll}>
            {s.loadingOlder ? (
              <div className="px-5 py-2 text-[0.6cqw]" style={{ color: TEXT_MUTED }}>{t('Loading older…')}</div>
            ) : null}
            {/* The root heads the thread and scrolls with it, ruled off
                from the replies, the way a Slack or Discord thread opens. */}
            {rootMsg ? (
              <div style={{ paddingBottom: '0.3cqw', marginBottom: '0.3cqw', borderBottom: `1px solid ${GOLD} 0.15)` }}>
                {renderRow(rootMsg, 0, [rootMsg], true)}
              </div>
            ) : null}
            {shown.length === 0 ? (
              <div className="px-5 py-6 text-[0.65cqw]" style={{ color: TEXT_MUTED }}>
                {searching ? t('Nothing matches “{query}”.', { query: query.trim() }) : threadId ? t('No replies yet. Start the thread.') : s.hydrated ? t('Quiet in here. Say something.') : t('Listening…')}
              </div>
            ) : shown.map((m, i) => renderRow(m, i, shown))}
          </div>
          {/* Always mounted so it can leave the way it came. */}
          <div
            className={`tavern-jump absolute left-1/2 bottom-2 flex items-center gap-2${showJump ? ' is-visible' : ''}`}
            aria-hidden={!showJump}
            style={{ padding: '0.2cqw 0.2cqw 0.2cqw 0.7cqw', borderRadius: 'var(--radius-full)', background: MENU_BG, border: `1px solid ${GOLD} 0.3)`, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.45)' }}
          >
            <span className="text-[0.58cqw] whitespace-nowrap" style={{ color: TEXT_BODY }}>
              {pendingBelow > 0 ? (pendingBelow === 1 ? t('1 new message') : t('{n} new messages', { n: pendingBelow })) : t('Viewing older messages')}
            </span>
            <button
              type="button"
              className="text-[0.58cqw] font-semibold cursor-pointer whitespace-nowrap"
              tabIndex={showJump ? 0 : -1}
              style={{ padding: '0.15cqw 0.6cqw', borderRadius: 'var(--radius-full)', background: `${GOLD} 0.15)`, border: `1px solid ${GOLD} 0.4)`, color: ACCENT_GOLD }}
              onClick={this.jumpToPresent}
            >
              {t('Jump to present')}
            </button>
          </div>
        </div>
        </div>

        <div className="shrink-0 relative" style={{ padding: `0.5cqw ${COMPOSER_GAP} ${COMPOSER_GAP}` }}>
          {replayPicker ? (
            <>
              <div className="fixed inset-0" style={{ zIndex: 19 }} onClick={() => this.setState({ replayPicker: false })} />
              <PopoverPanel style={{ position: 'absolute', bottom: '100%', left: COMPOSER_GAP, right: COMPOSER_GAP, zIndex: 20, maxHeight: '14cqw', overflowY: 'auto' }}>
                <PopoverMenuHeader>{t('Share a replay')}</PopoverMenuHeader>
                {replays.length === 0 ? (
                  <div className="text-xs italic" style={{ color: TEXT_MUTED, padding: '0.35cqw 0.75cqw 0.5cqw' }}>{t('No watchable matches yet.')}</div>
                ) : replays.map((m, i) => (
                  <PopoverMenuItem
                    key={`${m.roomCode}-${i}`}
                    label={m.customName || t('vs {name}', { name: m.opponentName || t('Opponent') })}
                    caption={[m.won ? t('Won') : t('Lost'), m.customName ? t('vs {name}', { name: m.opponentName || t('Opponent') }) : null, m.playedAt ? new Date(m.playedAt).toLocaleDateString() : null].filter(Boolean).join(' \u00b7 ')}
                    disabled={replayPicking}
                    onClick={() => this.pickReplay(m)}
                  />
                ))}
              </PopoverPanel>
            </>
          ) : null}
          {forwardId ? (
            <>
              <div className="fixed inset-0" style={{ zIndex: 19 }} onClick={() => this.setState({ forwardId: null })} />
              <PopoverPanel style={{ position: 'absolute', bottom: '100%', left: COMPOSER_GAP, right: COMPOSER_GAP, zIndex: 20, maxHeight: '14cqw', overflowY: 'auto' }}>
                <PopoverMenuHeader>{t('Forward to a friend')}</PopoverMenuHeader>
                <input
                  type="text"
                  value={forwardQuery}
                  placeholder={t('Search friends…')}
                  aria-label={t('Search friends')}
                  autoFocus
                  onInput={(e) => this.setState({ forwardQuery: e.currentTarget.value })}
                  className="w-full outline-none text-[0.6cqw]"
                  style={{ padding: '0.3cqw 0.75cqw', background: 'transparent', color: TEXT_BODY, border: 'none', caretColor: ACCENT_GOLD }}
                />
                {forwardFriends.length === 0 ? (
                  <div className="text-xs italic" style={{ color: TEXT_MUTED, padding: '0.35cqw 0.75cqw 0.5cqw' }}>{forwardNeedle ? t('No friend matches.') : t('No friends yet.')}</div>
                ) : forwardFriends.map((f) => {
                  const url = resolveAvatarUrl(f, sorceryCards);
                  return (
                    <PopoverMenuItem
                      key={f.id}
                      label={f.name}
                      caption={f.online ? t('Online') : undefined}
                      leading={url
                        ? <AvatarImage src={url} style={{ width: '1.25cqw', height: '1.25cqw', borderRadius: '50%' }} />
                        : <span className="inline-block" style={{ width: '1.25cqw', height: '1.25cqw', borderRadius: '50%', background: `${GOLD} 0.2)` }} />}
                      onClick={() => this.forwardTo(f)}
                    />
                  );
                })}
              </PopoverPanel>
            </>
          ) : null}
          <CommentComposer
            value={draft}
            onChange={(v) => { if (s.error) chat.clearError(); this.setState({ draft: v }); }}
            onSubmit={this.handleSubmit}
            posting={posting || waitLeft > 0}
            placeholder={replyingToRoot ? t('Reply to thread…') : replyTo ? t('Reply to {name}…', { name: replyTo.playerName }) : t('Say something…')}
            radius={compact ? undefined : innerRadius('var(--radius-2xl)', COMPOSER_GAP)}
            focusKey={this.state.composerFocus}
            sorceryCards={sorceryCards}
            mentionPlayers={this.mentionPlayers()}
            onOpenCard={this.handleCardLinkClick}
            onCardLinkHover={this.handleCardLinkHover}
            onCardLinkHoverEnd={this.handleCardLinkHoverEnd}
            canSendEmpty={!!replayDraft}
            attached={(replyTo && !replyingToRoot) || replayDraft ? (
              <>
                {replayDraft ? (
                  <ComposerBar clearLabel={t('Remove replay')} onClear={() => this.setState({ replayDraft: null })}>
                    {t('Sharing')} <span className="font-semibold" style={{ color: ACCENT_GOLD }}>{replayDraft.customName || `vs ${replayDraft.opponentName}`}</span>
                  </ComposerBar>
                ) : null}
                {replyTo && !replyingToRoot ? (
                  <ComposerBar clearLabel={t('Cancel reply')} onClear={() => this.setState({ replyTo: this.threadRootMessage() })}>
                    {t('Replying to')} <span className="font-semibold" style={{ color: ACCENT_GOLD }}>{replyTo.playerName}</span>
                  </ComposerBar>
                ) : null}
              </>
            ) : null}
            leading={(
              <button
                type="button"
                title={t('Share a replay')}
                aria-label={t('Share a replay')}
                className="flex items-center justify-center cursor-pointer transition-all"
                style={{
                  width: '1.85cqw',
                  height: '1.85cqw',
                  borderRadius: 'var(--radius-md)',
                  background: replayPicker ? `${GOLD} 0.12)` : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${replayPicker ? `${GOLD} 0.4)` : 'rgba(255,255,255,0.1)'}`,
                  color: replayPicker ? ACCENT_GOLD : TEXT_MUTED,
                }}
                onClick={() => { playUI(UI.CLICK); this.setState((st) => ({ replayPicker: !st.replayPicker })); }}
              >
                <ReplayIcon />
              </button>
            )}
          >
            {s.error ? (
              <div className="px-2 mb-1 text-[0.58cqw] tabular-nums" style={{ color: '#e89090' }}>{s.error}{waitLabel ? ` ${waitLabel}` : ''}</div>
            ) : null}
          </CommentComposer>
        </div>
        {cardHover ? <CardLinkPreview card={cardHover.card} rect={cardHover.rect} /> : null}
      </div>
    );
  }
}
